import React from 'react';
import PropTypes from 'prop-types';
import { LuBuilding, LuChevronDown, LuMapPin } from 'react-icons/lu';
import useSmoText from '../smoText';

/** One centre as a list row: name (full name on hover) and, for SMOs, its Primer / Backup tag on the right. */
const CenterRow = ({ center, showRole }) => {
  const { label } = useSmoText();
  const primer = center.isPrimerSmo;
  return (
    <li className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5 tw-px-3.5 tw-py-2">
      <LuBuilding aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
      <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-text-slate-800 dark:tw-text-slate-100" title={center.name}>{center.name}</span>
      {showRole && (
        <span
          title={primer ? label('centers.primerHint') : label('centers.backupHint')}
          className={`tw-grid tw-h-5 tw-w-5 tw-shrink-0 tw-place-items-center tw-rounded-md tw-text-[10px] tw-font-bold tw-text-white ${primer ? 'tw-bg-sky-500' : 'tw-bg-amber-500'}`}
        >
          {primer ? 'P' : 'B'}
        </span>
      )}
    </li>
  );
};

CenterRow.propTypes = { center: PropTypes.object.isRequired, showRole: PropTypes.bool };

const LIST = 'tw-divide-y tw-divide-slate-200/70 dark:tw-divide-white/[0.06]';

/**
 * Centres grouped by city (and by district above 10 centres in a city), as
 * collapsible cards; every city / district starts collapsed. `groups` is
 * `[{ city, centers }]` in display order. Used by SMO Lead and Bölge
 * Müdürleri Raporları (`accentColor` = region colour, `showRole` = P/B tags).
 */
const CityCenterGroups = ({ groups, keyPrefix, collapsedCities, setCollapsedCities, showRole, accentColor }) => {
  const { label } = useSmoText();
  const toggle = (key) => setCollapsedCities((prev) => ({ ...prev, [key]: !(prev[key] ?? true) }));
  if (groups.length === 0) return null;

  return (
    <>
      <div className="tw-grid tw-items-start tw-gap-2 md:tw-grid-cols-2">
        {groups.map(({ city, centers: centersInCity }) => {
          const cityKey = `${keyPrefix}-${city}`;
          const centerCount = centersInCity.length;
          // Tüm şehirler default kapalı gelsin
          const isCollapsed = collapsedCities[cityKey] ?? true;
          // İlçelere göre grupla (10'dan fazla merkez varsa)
          const groupByDistrict = centerCount > 10;
          const districtGroups = {};
          if (groupByDistrict) {
            centersInCity.forEach((center) => {
              const district = center.district || label('centers.unknownDistrict');
              if (!districtGroups[district]) districtGroups[district] = [];
              districtGroups[district].push(center);
            });
          }
          return (
            <div key={city} className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
              <button
                type="button"
                onClick={() => toggle(cityKey)}
                aria-expanded={!isCollapsed}
                // Accent colour as a CSS variable: colour utilities reading it survive the dark-mode shield, inline colours don't
                style={accentColor ? { '--accent': accentColor } : undefined}
                className={`tw-flex tw-w-full tw-items-center tw-gap-2 tw-border-l-4 tw-bg-transparent tw-px-3.5 tw-py-2.5 tw-text-left tw-text-sm hover:tw-bg-slate-900/[0.03] dark:hover:tw-bg-white/[0.04] ${accentColor ? 'tw-border-l-[color:var(--accent)]' : 'tw-border-slate-700 dark:tw-border-slate-300'}`}
              >
                <LuMapPin aria-hidden="true" className={`tw-h-4 tw-w-4 ${accentColor ? 'tw-text-[color:var(--accent)]' : 'tw-text-slate-400'}`} />
                <span className="tw-font-semibold tw-text-slate-900 dark:tw-text-white">{city}</span>
                <span className="tw-ml-auto tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                  {groupByDistrict
                    ? label('centers.cityCountDistricts', { count: centerCount, districts: Object.keys(districtGroups).length })
                    : label('centers.cityCount', { count: centerCount })}
                </span>
                <LuChevronDown aria-hidden="true" className={`tw-h-4 tw-w-4 tw-text-slate-400 tw-transition-transform ${isCollapsed ? '' : 'tw-rotate-180'}`} />
              </button>

              {!isCollapsed && (
                <div className="tw-border-t tw-border-slate-200/70 dark:tw-border-white/10">
                  {groupByDistrict ? (
                    // İlçe bazlı gruplandırma
                    Object.entries(districtGroups)
                      .sort((a, b) => a[0].localeCompare(b[0], 'tr'))
                      .map(([district, centers]) => {
                        const districtKey = `${cityKey}-${district}`;
                        const isDistrictCollapsed = collapsedCities[districtKey] ?? true;
                        return (
                          <div key={district} className="tw-border-b tw-border-slate-200/70 last:tw-border-b-0 dark:tw-border-white/10">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggle(districtKey);
                              }}
                              aria-expanded={!isDistrictCollapsed}
                              className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-bg-slate-500/[0.04] tw-px-3.5 tw-py-2 tw-text-left tw-text-xs hover:tw-bg-slate-500/[0.08] dark:tw-bg-white/[0.02] dark:hover:tw-bg-white/[0.05]"
                            >
                              <span className="tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{district}</span>
                              <span className="tw-ml-auto tw-text-slate-500 dark:tw-text-slate-400">{label('centers.districtCount', { count: centers.length })}</span>
                              <LuChevronDown aria-hidden="true" className={`tw-h-3.5 tw-w-3.5 tw-text-slate-400 tw-transition-transform ${isDistrictCollapsed ? '' : 'tw-rotate-180'}`} />
                            </button>
                            {!isDistrictCollapsed && (
                              <ul className={LIST}>
                                {centers.map((center) => <CenterRow key={center.id} center={center} showRole={showRole} />)}
                              </ul>
                            )}
                          </div>
                        );
                      })
                  ) : (
                    // Direkt merkez listesi (10'dan az merkez)
                    <ul className={LIST}>
                      {centersInCity.map((center) => <CenterRow key={center.id} center={center} showRole={showRole} />)}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
        {showRole && <span className="tw-inline-flex tw-items-center tw-gap-1"><span className="tw-rounded tw-bg-sky-500 tw-px-1.5 tw-text-[10px] tw-font-bold tw-text-white">P</span>{label('centers.legendPrimer')}</span>}
        {showRole && <span className="tw-inline-flex tw-items-center tw-gap-1"><span className="tw-rounded tw-bg-amber-500 tw-px-1.5 tw-text-[10px] tw-font-bold tw-text-white">B</span>{label('centers.legendBackup')}</span>}
        <span>{label('centers.legendToggle')}</span>
      </p>
    </>
  );
};

CityCenterGroups.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.shape({ city: PropTypes.string, centers: PropTypes.array })).isRequired,
  keyPrefix: PropTypes.string.isRequired,
  collapsedCities: PropTypes.object.isRequired,
  setCollapsedCities: PropTypes.func.isRequired,
  showRole: PropTypes.bool,
  accentColor: PropTypes.string,
};

export default CityCenterGroups;
